import type { FailedTx, Repository, SyncController } from '@ims/data'
import { CLOCK_BACK_TOLERANCE_MS, STALE_AFTER_MS } from '@ims/local'

// What to say about a device that has been offline for a while
// (docs/plans/offline-safety.md). The engine enforces the lock itself
// (SyncEngine.isStale); this only decides the warnings.

/** Changes waiting this long get a banner: don't uninstall or clear browser data yet. */
export const UNSYNCED_NOTICE_MINUTES = 30

/** The warning starts this long before the lock. */
const WARN_BEFORE_MS = 24 * 60 * 60 * 1000

export type OfflineLock =
  | { kind: 'ok' }
  /** Billing stops in `hoursLeft` hours unless the device syncs. */
  | { kind: 'soon'; hoursLeft: number }
  /** Billing, payments and stock are paused until the device syncs. */
  | { kind: 'stale' }

/**
 * Mirrors SyncEngine.isStale: 3 days since the last check-in, or a clock moved
 * back well before it. A check-in a moment after `now` is normal (it happened
 * after `now` was read), not a clock moved back.
 */
export function offlineLock(lastCheckinAt: string | null, now: Date): OfflineLock {
  if (!lastCheckinAt) return { kind: 'ok' }
  const since = now.getTime() - new Date(lastCheckinAt).getTime()
  if (since < -CLOCK_BACK_TOLERANCE_MS || since > STALE_AFTER_MS) return { kind: 'stale' }
  if (since < 0) return { kind: 'ok' }
  const left = STALE_AFTER_MS - since
  if (left <= WARN_BEFORE_MS)
    return { kind: 'soon', hoursLeft: Math.max(1, Math.ceil(left / 3_600_000)) }
  return { kind: 'ok' }
}

/** True when changes have waited long enough to warn against uninstalling or clearing data. */
export function unsyncedTooLong(oldestUnsyncedAt: string | null, now: Date): boolean {
  return (
    oldestUnsyncedAt !== null &&
    now.getTime() - new Date(oldestUnsyncedAt).getTime() >= UNSYNCED_NOTICE_MINUTES * 60_000
  )
}

// Sign-out deletes every business's data on this device, so it must know how
// many changes wait in the businesses that are not open. Each open store
// records its own count here (per-device, so localStorage).
const unsyncedKey = (userId: string, tenantId: string) => `ims.unsynced.${userId}.${tenantId}`

export function recordUnsynced(userId: string, tenantId: string, count: number): void {
  try {
    if (count > 0) localStorage.setItem(unsyncedKey(userId, tenantId), String(count))
    else localStorage.removeItem(unsyncedKey(userId, tenantId))
  } catch {
    // Storage blocked: sign-out still checks the open business.
  }
}

export function readUnsynced(userId: string, tenantId: string): number {
  try {
    const n = Number(localStorage.getItem(unsyncedKey(userId, tenantId)))
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

export function forgetUnsynced(userId: string, tenantId: string): void {
  recordUnsynced(userId, tenantId, 0)
}

/**
 * Bills this month on this device's copy (finalized and cancelled, as the plan
 * limit counts them), by invoice date.
 */
export async function monthInvoiceCount(repo: Pick<Repository, 'invoices'>): Promise<number> {
  const today = new Date()
  const from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
  const [finalized, cancelled] = await Promise.all([
    repo.invoices.list({ status: 'finalized', from, limit: 1 }),
    repo.invoices.list({ status: 'cancelled', from, limit: 1 }),
  ])
  return finalized.total + cancelled.total
}

/**
 * The bill as it is now, for printing right after the sale (the Sale complete dialog). A
 * bill on the server-numbered ("web") counter has no number until it syncs; the dialog's
 * copy was taken before that. So read it again, and while it still has no number and the
 * device is online, sync and wait (at most `ms`) for the number. Offline, or when the
 * server is slow, it comes back without one ("Number assigned on sync").
 */
export async function billWithNumber<T extends { number: string | null; status: string }>(
  read: () => Promise<T | null | undefined>,
  engine: Pick<SyncController, 'syncNow'>,
  online: boolean,
  fallback: T,
  ms = 5000,
): Promise<T> {
  const now = (await read()) ?? fallback
  if (now.number || now.status !== 'finalized' || !online) return now
  await Promise.race([
    engine.syncNow().catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ])
  return (await read()) ?? now
}

/**
 * Right after a bill is saved, while online: sync and wait (at most `ms`) for
 * the server's answer, so a refusal shows before the bill is printed. Returns
 * the refusal, 'ok' when the server took it, or 'unknown' (slow or offline).
 */
export async function confirmWithServer(
  engine: Pick<SyncController, 'syncNow' | 'state'>,
  txId: string,
  ms = 3000,
): Promise<FailedTx | 'ok' | 'unknown'> {
  let finished = false
  await Promise.race([
    engine
      .syncNow()
      .then(() => {
        finished = true
      })
      .catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ])
  const state = engine.state()
  const refused = state.failed.find((f) => f.txId === txId)
  if (refused) return refused
  return finished && state.phase === 'idle' ? 'ok' : 'unknown'
}
